/**
 * Data upload: any file -> our own template workbook (`POST data-upload/convert`).
 *
 * A school uploads its data in whatever shape it has (PDF, Word, a photo of a register, a
 * spreadsheet in its own layout). The backend reads it and hands back OUR template, filled in;
 * the portal then feeds that workbook to the parser it already uses for a hand-filled template.
 * Nothing here saves data. AI servers only: on other servers the routes answer 404.
 *
 * Everything in this file is pure (no React, no redux) so it can be unit tested; the
 * components own dispatching and state.
 */
import type {TranslationKey} from '_metronic/i18n/translationKeys'

// --- Types ------------------------------------------------------------------------------

export type DataUploadUseCaseKey = 'teachers' | 'students' | 'allocation'
export type DataUploadFlavour = 'onboarding' | 'institute'

/** The workbook returned by a conversion, base64 encoded. */
export interface DataUploadTemplate {
  key?: string
  fileName: string
  contentType: string
  base64: string
}

/** A row left OUT of the template, with the reason. */
export interface DataUploadSkippedRow {
  recordId?: string
  label?: string
  reason?: string
  /** The row's own error under `reason`, e.g. `NAME_NOT_ENGLISH` under `INVALID`. */
  code?: string
  message?: string
}

/** A row that was written, but is worth a second look. */
export interface DataUploadReviewRow {
  recordId?: string
  label?: string
  fields?: string[]
  /** Fields left blank because the server could not fill them (`class`, `registrationNumber`). */
  missing?: string[]
  /** Candidates for a blank field, e.g. two curricula that both have "Grade 1-A". */
  choices?: Record<string, string[]>
}

/**
 * The numbers behind a response's plain `message`: rows read, rows kept, and how many were left
 * out (or need a value) per code.
 */
export interface DataUploadCounts {
  read: number | null
  kept: number | null
  leftOut: Record<string, number>
  needValues: Record<string, number>
}

export interface DataUploadReport {
  /** Rows found in the upload; null on passthrough. */
  read: number | null
  /** Rows written into the template. */
  written: number
  /** Students only: saved students carried over next to the new ones. */
  kept: number
  skipped: DataUploadSkippedRow[]
  needsReview: DataUploadReviewRow[]
}

export interface DataUploadFileResult {
  fileName?: string
  status?: 'ok' | 'no_relevant_data' | 'error' | string
  /** `template`, `deterministic` (read exactly, no AI), `ai_text`, `ai_ocr`, `ai_vision`. */
  route?: string
  reason?: string
  error?: {code?: string; message?: string} | null
}

/** What a screen needs from a conversion, whichever endpoint produced it. */
export interface DataUploadConversion {
  passthrough: boolean
  template: DataUploadTemplate | null
  report: DataUploadReport
  files: DataUploadFileResult[]
  draftId: string | null
  /** The server's plain summary ("Read 52 rows. 50 teachers are ready…"), when it sent one. */
  message?: string | null
  counts?: DataUploadCounts | null
}

export interface DataUploadConfig {
  maxFileBytes: number
  maxFiles: number
  /** Lower-cased extensions without the dot. */
  formats: string[]
}

// --- Limits & formats -------------------------------------------------------------------

/**
 * Used until `GET data-upload/use-cases` answers, and when it cannot be reached. Files travel in
 * 512 KB pieces (common/helpers/chunkedUpload), so nginx's body cap no longer sets the limit;
 * the server's own `limits.maxFileBytes` (50 MB) does, and this mirrors it.
 */
export const DEFAULT_DATA_UPLOAD_CONFIG: DataUploadConfig = {
  maxFileBytes: 50 * 1024 * 1024,
  maxFiles: 10,
  formats: [
    'xlsx',
    'csv',
    'txt',
    'ods',
    'docx',
    'odt',
    'pdf',
    'png',
    'jpg',
    'jpeg',
    'webp',
    'heic',
    'heif',
  ],
}

/** Formats the browser-side parsers read themselves; everything else needs a conversion. */
const SPREADSHEET_FORMATS: readonly string[] = ['xlsx', 'csv']
const IMAGE_FORMATS: readonly string[] = ['png', 'jpg', 'jpeg', 'webp', 'heic', 'heif']

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : null

const firstPositiveNumber = (...values: unknown[]): number | null => {
  for (const value of values) {
    const n = Number(value)
    if (Number.isFinite(n) && n > 0) return n
  }
  return null
}

/** Lower-cased extension of a file name, without the dot ("" when there is none). */
export const dataUploadFileExtension = (name: string): string => {
  const idx = name.lastIndexOf('.')
  return idx >= 0 ? name.slice(idx + 1).toLowerCase() : ''
}

export const isSpreadsheetUpload = (file: Pick<File, 'name'>): boolean =>
  SPREADSHEET_FORMATS.includes(dataUploadFileExtension(file.name))

export const isImageUpload = (file: Pick<File, 'name'>): boolean =>
  IMAGE_FORMATS.includes(dataUploadFileExtension(file.name))

/**
 * Limits and accepted formats out of the `data-upload/use-cases` response. The limits are
 * never hardcoded by the screens; anything the response does not state keeps its default.
 *
 * @param response The API response (envelope or bare `result`).
 * @param useCaseKey Narrows the formats to that use case's `acceptedFormats` when it lists any.
 */
export const parseDataUploadConfig = (
  response: unknown,
  useCaseKey?: DataUploadUseCaseKey
): DataUploadConfig => {
  const envelope = asRecord(response)
  const result = asRecord(envelope?.result) ?? envelope
  const limits = asRecord(result?.limits)

  const megabytes = firstPositiveNumber(limits?.maxFileMb, limits?.maxFileMB, limits?.perFileMb)
  const maxFileBytes =
    firstPositiveNumber(
      limits?.maxFileBytes,
      limits?.maxFileSizeBytes,
      limits?.perFileBytes,
      limits?.maxFileSize,
      limits?.fileSizeBytes
    ) ?? (megabytes ? megabytes * 1024 * 1024 : null)
  const maxFiles = firstPositiveNumber(
    limits?.maxFiles,
    limits?.maxFilesPerRequest,
    limits?.filesPerRequest
  )

  const rawUseCases = result?.useCases ?? result?.use_cases ?? result?.items
  const useCases: unknown[] = Array.isArray(rawUseCases)
    ? rawUseCases
    : Array.isArray(result)
      ? (result as unknown[])
      : Object.entries(asRecord(rawUseCases) ?? {}).map(([key, value]) => ({
          key,
          ...(asRecord(value) ?? {}),
        }))
  const useCase = useCases
    .map(asRecord)
    .find((entry) => entry && (entry.key ?? entry.useCaseKey) === useCaseKey)
  const rawFormats = useCase?.acceptedFormats ?? limits?.acceptedFormats ?? limits?.formats
  const formats = Array.isArray(rawFormats)
    ? rawFormats
        .map((format) =>
          String(format ?? '')
            .trim()
            .toLowerCase()
            .replace(/^\./, '')
        )
        .filter(Boolean)
    : []

  return {
    maxFileBytes: maxFileBytes ?? DEFAULT_DATA_UPLOAD_CONFIG.maxFileBytes,
    maxFiles: maxFiles ?? DEFAULT_DATA_UPLOAD_CONFIG.maxFiles,
    formats: formats.length > 0 ? formats : DEFAULT_DATA_UPLOAD_CONFIG.formats,
  }
}

/** `accept` attribute value for a file input. */
export const dataUploadAcceptAttribute = (formats: readonly string[]): string =>
  formats.map((format) => `.${format}`).join(',')

/** "7 MB" style label for the limits copy. */
export const formatDataUploadBytes = (bytes: number): string => {
  const mb = bytes / (1024 * 1024)
  return `${Number.isInteger(mb) ? mb : mb.toFixed(1)} MB`
}

/**
 * Pre-flight check of one file, run before anything is sent. Returns the backend's own code for
 * the refusal (so both share one translation), or null when the file may go.
 */
export const validateDataUploadFile = (
  file: Pick<File, 'name' | 'size'>,
  config: DataUploadConfig = DEFAULT_DATA_UPLOAD_CONFIG
): 'FILE_TYPE_NOT_ALLOWED' | 'TOO_LARGE' | 'EMPTY' | null => {
  if (!config.formats.includes(dataUploadFileExtension(file.name))) return 'FILE_TYPE_NOT_ALLOWED'
  if (file.size === 0) return 'EMPTY'
  if (file.size > config.maxFileBytes) return 'TOO_LARGE'
  return null
}

// --- Request ----------------------------------------------------------------------------

/**
 * FormData for `POST data-upload/convert`. Non-file fields are strings. `branch-id` travels as
 * a header (added by the saga), which the backend uses to tell same-named classes apart.
 */
export const buildDataUploadConvertForm = (
  files: readonly File[],
  options: {
    useCaseKey: DataUploadUseCaseKey
    flavour?: DataUploadFlavour
    /** Students, onboarding flavour: ask for the layout with a separate Curriculum column. */
    curriculumColumn?: boolean
    /** A file already sent in pieces: sent instead of `files`. */
    uploadId?: string | null
  }
): FormData => {
  const form = new FormData()
  if (options.uploadId) form.append('uploadId', options.uploadId)
  else files.forEach((file) => form.append('files', file, file.name))
  form.append('useCaseKey', options.useCaseKey)
  if (options.flavour) form.append('flavour', options.flavour)
  if (options.curriculumColumn) form.append('curriculum_column', 'true')
  return form
}

// --- Response ---------------------------------------------------------------------------

const toArray = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : [])

const toCountMap = (value: unknown): Record<string, number> => {
  const out: Record<string, number> = {}
  Object.entries(asRecord(value) ?? {}).forEach(([code, count]) => {
    const n = Number(count)
    if (Number.isFinite(n) && n > 0) out[code] = n
  })
  return out
}

const toCount = (value: unknown): number | null => {
  const n = Number(value)
  return value !== null && value !== undefined && Number.isFinite(n) ? n : null
}

const toMessage = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null

/** `counts` of a data-upload answer, or null when it has none. */
export const normalizeDataUploadCounts = (value: unknown): DataUploadCounts | null => {
  const counts = asRecord(value)
  if (!counts) return null
  return {
    read: toCount(counts.read),
    kept: toCount(counts.kept ?? counts.written),
    leftOut: toCountMap(counts.leftOut),
    needValues: toCountMap(counts.needValues),
  }
}

const toTemplate = (value: unknown): DataUploadTemplate | null => {
  const template = asRecord(value)
  const base64 = typeof template?.base64 === 'string' ? template.base64 : ''
  if (!template || !base64) return null
  return {
    key: typeof template.key === 'string' ? template.key : undefined,
    fileName: String(template.fileName ?? 'Template.xlsx'),
    contentType: String(
      template.contentType ?? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ),
    base64,
  }
}

/**
 * Normalizes the `result` of `data-upload/convert`, or the `conversion` field that
 * `csv-import/upload` adds when it converted the file first. Null when `value` is neither.
 */
export const normalizeDataUploadConversion = (value: unknown): DataUploadConversion | null => {
  const data = asRecord(value)
  if (!data || (!('report' in data) && !('template' in data) && !('passthrough' in data))) {
    return null
  }
  const report = asRecord(data.report)
  const read = report?.read
  return {
    passthrough: data.passthrough === true,
    template: toTemplate(data.template),
    report: {
      read: typeof read === 'number' ? read : null,
      written: Number(report?.written ?? 0) || 0,
      kept: Number(report?.kept ?? 0) || 0,
      skipped: toArray<DataUploadSkippedRow>(report?.skipped),
      needsReview: toArray<DataUploadReviewRow>(report?.needsReview),
    },
    files: toArray<DataUploadFileResult>(data.files),
    draftId: typeof data.draftId === 'string' ? data.draftId : null,
    message: toMessage(data.message),
    counts: normalizeDataUploadCounts(data.counts),
  }
}

/** True when at least one file was read by AI: those rows deserve a "please check" badge. */
export const wasReadByAi = (conversion: Pick<DataUploadConversion, 'files'>): boolean =>
  conversion.files.some((file) => String(file.route ?? '').startsWith('ai_'))

/** The workbook as a `File`, ready for whatever handles a template upload on that screen. */
export const dataUploadTemplateToFile = (template: DataUploadTemplate): File => {
  const bytes = Uint8Array.from(atob(template.base64), (c) => c.charCodeAt(0))
  return new File([bytes], template.fileName, {type: template.contentType})
}

/** Saves the filled template, so the user can fix it in Excel and re-upload it (no AI then). */
export const downloadDataUploadTemplate = (template: DataUploadTemplate): void => {
  const url = URL.createObjectURL(dataUploadTemplateToFile(template))
  const link = document.createElement('a')
  link.href = url
  link.download = template.fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// --- Errors -----------------------------------------------------------------------------

/**
 * Translated copy per backend code. Codes arrive in `result.code` (never in an `error` field),
 * or in `files[].error.code` for a single bad file.
 */
export const DATA_UPLOAD_ERROR_KEYS = {
  NO_FILES: 'DATA_UPLOAD.CONVERT.ERROR.NO_FILES',
  TOO_MANY_FILES: 'DATA_UPLOAD.CONVERT.ERROR.TOO_MANY_FILES',
  TOO_LARGE: 'DATA_UPLOAD.CONVERT.ERROR.TOO_LARGE',
  FILE_TYPE_NOT_ALLOWED: 'DATA_UPLOAD.CONVERT.ERROR.FILE_TYPE_NOT_ALLOWED',
  FILE_SIGNATURE_MISMATCH: 'DATA_UPLOAD.CONVERT.ERROR.FILE_SIGNATURE_MISMATCH',
  ARCHIVE_UNSAFE: 'DATA_UPLOAD.CONVERT.ERROR.ARCHIVE_UNSAFE',
  EMPTY: 'DATA_UPLOAD.CONVERT.ERROR.EMPTY',
  TOO_MANY_PAGES: 'DATA_UPLOAD.CONVERT.ERROR.TOO_MANY_PAGES',
  PARSE_FAILED: 'DATA_UPLOAD.CONVERT.ERROR.PARSE_FAILED',
  NO_RELEVANT_DATA: 'DATA_UPLOAD.CONVERT.ERROR.NO_RELEVANT_DATA',
  AI_UNAVAILABLE: 'DATA_UPLOAD.CONVERT.ERROR.AI_UNAVAILABLE',
  AI_DISABLED: 'DATA_UPLOAD.CONVERT.ERROR.AI_UNAVAILABLE',
  AI_BUDGET_EXCEEDED: 'DATA_UPLOAD.CONVERT.ERROR.AI_BUDGET_EXCEEDED',
  USE_CASE_REQUIRED: 'DATA_UPLOAD.CONVERT.ERROR.USE_CASE_REQUIRED',
  USE_CASE_NOT_SUPPORTED: 'DATA_UPLOAD.CONVERT.ERROR.USE_CASE_NOT_SUPPORTED',
  NO_ROWS_CONVERTED: 'DATA_UPLOAD.CONVERT.ERROR.NO_ROWS_CONVERTED',
  FORBIDDEN: 'DATA_UPLOAD.CONVERT.ERROR.FORBIDDEN',
  DRAFT_NOT_FOUND: 'DATA_UPLOAD.CONVERT.ERROR.DRAFT_NOT_FOUND',
  JOB_RUNNING: 'DATA_UPLOAD.CONVERT.ERROR.JOB_RUNNING',
  VALIDATION_REQUIRED: 'DATA_UPLOAD.CONVERT.ERROR.VALIDATION_REQUIRED',
  // Row checks: a file's `reason` when every row failed one, or `skipped[].code`. Take {count}.
  NAME_NOT_ENGLISH: 'DATA_UPLOAD.CONVERT.ERROR.NAME_NOT_ENGLISH',
  NAME_HAS_DIGITS: 'DATA_UPLOAD.CONVERT.ERROR.NAME_HAS_DIGITS',
  NOT_A_NAME: 'DATA_UPLOAD.CONVERT.ERROR.NOT_A_NAME',
  NOT_A_RECORD: 'DATA_UPLOAD.CONVERT.ERROR.NOT_A_RECORD',
  // Chunked upload (common/helpers/chunkedUpload).
  UPLOAD_NOT_FOUND: 'DATA_UPLOAD.CONVERT.ERROR.UPLOAD_NOT_FOUND',
  UPLOAD_INCOMPLETE: 'DATA_UPLOAD.CONVERT.ERROR.UPLOAD_INCOMPLETE',
} satisfies Record<string, TranslationKey>

/** Codes that describe rows rather than the file; their copy reads "{count} names are…". */
export const DATA_UPLOAD_ROW_CODES = [
  'NAME_NOT_ENGLISH',
  'NAME_HAS_DIGITS',
  'NOT_A_NAME',
  'NOT_A_RECORD',
] as const

export type DataUploadRowCode = (typeof DATA_UPLOAD_ROW_CODES)[number]

const isDataUploadRowCode = (code: string | null | undefined): code is DataUploadRowCode =>
  Boolean(code && (DATA_UPLOAD_ROW_CODES as readonly string[]).includes(code))

/** The same rules said about one left-out row (`report.skipped[]`). */
const DATA_UPLOAD_ROW_CODE_KEYS = {
  NAME_NOT_ENGLISH: 'DATA_UPLOAD.CONVERT.SKIP.NAME_NOT_ENGLISH',
  NAME_HAS_DIGITS: 'DATA_UPLOAD.CONVERT.SKIP.NAME_HAS_DIGITS',
  NOT_A_NAME: 'DATA_UPLOAD.CONVERT.SKIP.NOT_A_NAME',
  NOT_A_RECORD: 'DATA_UPLOAD.CONVERT.SKIP.NOT_A_RECORD',
} satisfies Record<DataUploadRowCode, TranslationKey>

export type DataUploadErrorCode = keyof typeof DATA_UPLOAD_ERROR_KEYS

/** `result.code` of a failed call (the saga hands the whole envelope to `onFailure`). */
export const dataUploadErrorCode = (error: unknown): string | null => {
  if (typeof error === 'string') return error || null
  const envelope = asRecord(error)
  const nested = asRecord(asRecord(envelope?.response)?.data)
  const result = asRecord(envelope?.result) ?? asRecord(nested?.result)
  const code = result?.code ?? envelope?.code
  return typeof code === 'string' && code ? code : null
}

/** HTTP status of a failed call: the envelope's `statusCode`, or a proxy error page's status. */
export const dataUploadErrorStatus = (error: unknown): number | null => {
  const envelope = asRecord(error)
  const status = Number(envelope?.statusCode ?? envelope?.status)
  return Number.isFinite(status) && status > 0 ? status : null
}

/** The `conversion` that a failed `csv-import/upload` carries next to its code, if any. */
export const dataUploadErrorConversion = (error: unknown): DataUploadConversion | null => {
  const envelope = asRecord(error)
  const result = asRecord(envelope?.result)
  return normalizeDataUploadConversion(result?.conversion)
}

/** Translation key for a backend code; null when the code is unknown (show `message` then). */
export const dataUploadErrorKey = (code: string | null | undefined): TranslationKey | null =>
  code && code in DATA_UPLOAD_ERROR_KEYS
    ? DATA_UPLOAD_ERROR_KEYS[code as DataUploadErrorCode]
    : null

/**
 * The first per-file failure of a conversion that otherwise succeeded. One bad file does not
 * fail the request: the other files' rows still come back.
 */
export const firstDataUploadFileError = (
  conversion: Pick<DataUploadConversion, 'files'>
): {code: string; message?: string} | null => {
  for (const file of conversion.files) {
    if (file.status === 'error' && file.error?.code) {
      return {code: file.error.code, message: file.error.message ?? undefined}
    }
    if (file.status === 'no_relevant_data') return {code: 'NO_RELEVANT_DATA'}
  }
  return null
}

/** Why a row was left out: translated meaning per `report.skipped[].reason`. */
export const DATA_UPLOAD_SKIP_REASON_KEYS = {
  MISSING_REQUIRED: 'DATA_UPLOAD.CONVERT.SKIP.MISSING_REQUIRED',
  MISSING_REGISTRATION: 'DATA_UPLOAD.CONVERT.SKIP.MISSING_REGISTRATION',
  UNRESOLVED: 'DATA_UPLOAD.CONVERT.SKIP.UNRESOLVED',
  NO_SLOT: 'DATA_UPLOAD.CONVERT.SKIP.NO_SLOT',
  CONFLICT: 'DATA_UPLOAD.CONVERT.SKIP.CONFLICT',
  DUPLICATE: 'DATA_UPLOAD.CONVERT.SKIP.DUPLICATE',
  ALREADY_EXISTS: 'DATA_UPLOAD.CONVERT.SKIP.ALREADY_EXISTS',
  INVALID: 'DATA_UPLOAD.CONVERT.SKIP.INVALID',
  EXCLUDED: 'DATA_UPLOAD.CONVERT.SKIP.EXCLUDED',
} satisfies Record<string, TranslationKey>

export const dataUploadSkipReasonKey = (reason: string | undefined): TranslationKey | null =>
  reason && reason in DATA_UPLOAD_SKIP_REASON_KEYS
    ? DATA_UPLOAD_SKIP_REASON_KEYS[reason as keyof typeof DATA_UPLOAD_SKIP_REASON_KEYS]
    : null

/**
 * Translation for one left-out row: its own `code` first (Students: `NAME_NOT_ENGLISH` under
 * reason `INVALID`; Teachers: the row code itself), then its `reason`.
 */
export const dataUploadSkippedRowKey = (
  row: Pick<DataUploadSkippedRow, 'code' | 'reason'>
): TranslationKey | null => {
  if (isDataUploadRowCode(row.code)) return DATA_UPLOAD_ROW_CODE_KEYS[row.code]
  if (isDataUploadRowCode(row.reason)) return DATA_UPLOAD_ROW_CODE_KEYS[row.reason]
  return dataUploadSkipReasonKey(row.reason)
}

type FormatMessage = (descriptor: {id: TranslationKey}, values?: Record<string, any>) => string

/**
 * User-facing copy for a failed upload or conversion. The code is mapped to translated copy;
 * the backend `message` is only a fallback for a code this build does not know. Without a code
 * the HTTP status decides: a proxy's 413 is "too large", a 408/502/504 "took too long". A call
 * that got no answer at all (the connection dropped) says so.
 *
 * @param error Either a code, or whatever the saga handed to `onFailure`.
 * @param context.count For a row code (`NAME_NOT_ENGLISH`…): how many rows it hit.
 */
export const dataUploadErrorMessage = (
  formatMessage: FormatMessage,
  error: unknown,
  context: {
    config?: DataUploadConfig
    useCaseKey?: DataUploadUseCaseKey
    fileName?: string
    count?: number
  } = {}
): string => {
  const envelope = asRecord(error)
  const code = dataUploadErrorCode(error)
  const config = context.config ?? DEFAULT_DATA_UPLOAD_CONFIG
  const values = {
    n: config.maxFiles,
    size: formatDataUploadBytes(config.maxFileBytes),
    useCase: context.useCaseKey ?? 'other',
    count: context.count ?? 1,
  }

  const extension = context.fileName ? dataUploadFileExtension(context.fileName) : ''
  if (code === 'FILE_TYPE_NOT_ALLOWED' && (extension === 'xls' || extension === 'doc')) {
    return formatMessage({id: 'DATA_UPLOAD.CONVERT.ERROR.LEGACY_FORMAT'})
  }
  const key = dataUploadErrorKey(code)
  if (key) return formatMessage({id: key}, values)

  const status = dataUploadErrorStatus(error)
  if (status === 404) return formatMessage({id: 'DATA_UPLOAD.CONVERT.ERROR.NOT_AVAILABLE'})
  if (status === 403) return formatMessage({id: 'DATA_UPLOAD.CONVERT.ERROR.FORBIDDEN'})
  if (status === 413) return formatMessage({id: 'DATA_UPLOAD.CONVERT.ERROR.TOO_LARGE'}, values)
  if (status === 408 || status === 502 || status === 504) {
    return formatMessage({id: 'DATA_UPLOAD.CONVERT.ERROR.TIMED_OUT'})
  }
  // No answer at all: fetch itself failed (connection dropped, or a proxy page without CORS).
  if (error instanceof Error || (!code && status === null)) {
    return formatMessage({id: 'DATA_UPLOAD.CONVERT.ERROR.CONNECTION_LOST'})
  }
  const message = toMessage(asRecord(envelope?.result)?.message) ?? toMessage(envelope?.message)
  return message || formatMessage({id: 'GENERAL.SOMETHING_WENT_WRONG'})
}

/**
 * A translated summary built from `counts`: "Read 52 rows. 50 teachers are ready. 1 line is a
 * heading or note, not a person." Null when a left-out code has no translation here; the caller
 * shows the server's own `message` then, which is always complete.
 */
export const dataUploadCountsSummary = (
  formatMessage: FormatMessage,
  counts: DataUploadCounts | null | undefined,
  useCaseKey: DataUploadUseCaseKey
): string | null => {
  if (!counts || counts.read === null || counts.kept === null) return null
  const lines = [
    formatMessage(
      {id: 'DATA_UPLOAD.CONVERT.COUNTS.READ_KEPT'},
      {read: counts.read, kept: counts.kept, useCase: useCaseKey}
    ),
  ]
  for (const [code, count] of Object.entries(counts.leftOut)) {
    if (!isDataUploadRowCode(code)) return null
    lines.push(formatMessage({id: DATA_UPLOAD_ERROR_KEYS[code]}, {count}))
  }
  return lines.join(' ')
}

// --- Drafts: the Teachers file carried forward to Students / Allocation -----------------

/** Title `onboarding/prefill` gives its drafts. */
export const ONBOARDING_PREFILL_DRAFT_TITLE = 'onboarding-prefill'

export interface DataUploadDraftSummary {
  id: string
  /** `queued`, `processing`, `done`, `failed`, `cancelled`; null when the list left it out. */
  jobStatus: string | null
}

const RUNNING_JOB_STATUSES = [
  'queued',
  'pending',
  'processing',
  'running',
  'in_progress',
  'started',
]
const DONE_JOB_STATUSES = ['done', 'completed', 'complete', 'succeeded', 'success', 'finished']
const FAILED_JOB_STATUSES = ['failed', 'error', 'cancelled', 'canceled']

/**
 * A job's status as `queued` / `processing` / `done` / `failed` / `cancelled`, or null when
 * there is none. Synonyms are folded in; anything else (a bare job id where a job object was
 * expected) is null, never mistaken for a status.
 *
 * `interrupted` (node-api reports it even for a job still queued, with `finishedAt: null`)
 * counts as still running until the job has a `finishedAt`; after that it failed.
 */
const toJobStatus = (value: unknown): string | null => {
  const raw = asRecord(value)?.status ?? asRecord(value)?.state ?? value
  if (typeof raw !== 'string') return null
  const status = raw
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
  if (status === 'interrupted') return asRecord(value)?.finishedAt ? 'failed' : 'processing'
  if (RUNNING_JOB_STATUSES.includes(status)) return status === 'queued' ? 'queued' : 'processing'
  if (DONE_JOB_STATUSES.includes(status)) return 'done'
  if (FAILED_JOB_STATUSES.includes(status))
    return status === 'failed' || status === 'error' ? 'failed' : 'cancelled'
  return null
}

/** The newest draft of `GET data-upload/drafts` (`result.drafts[0]`), or null. */
export const newestDataUploadDraft = (response: unknown): DataUploadDraftSummary | null => {
  const envelope = asRecord(response)
  const result = envelope?.result ?? envelope
  const drafts = Array.isArray(result) ? result : toArray<unknown>(asRecord(result)?.drafts)
  const draft = asRecord(drafts[0])
  const id = draft?.id ?? draft?.draftId
  if (typeof id !== 'string' && typeof id !== 'number') return null
  return {id: String(id), jobStatus: toJobStatus(draft?.job ?? draft?.jobStatus)}
}

/** `job.status` of `GET data-upload/drafts/:id/job`. */
export const dataUploadJobStatus = (response: unknown): string | null => {
  const envelope = asRecord(response)
  const result = asRecord(envelope?.result) ?? envelope
  return toJobStatus(result?.job ?? result?.status)
}

/** `job.progress.percent` (0..100) of `GET data-upload/drafts/:id/job`, or null. */
export const dataUploadJobPercent = (response: unknown): number | null => {
  const envelope = asRecord(response)
  const result = asRecord(envelope?.result) ?? envelope
  const job = asRecord(result?.job) ?? result
  const percent = Number(asRecord(job?.progress)?.percent)
  return Number.isFinite(percent) ? Math.min(100, Math.max(0, Math.round(percent))) : null
}

/** True while a draft's job is still reading: its template call answers 409 then. */
export const isDataUploadJobRunning = (status: string | null | undefined): boolean =>
  status === 'queued' || status === 'processing'
